<template>
  <main
    role="main"
    aria-label="Contenu principal"
  >
    <h1 class="hero-title">
      Création de sites web sur mesure à Strasbourg
      <span class="subtitle">Boostez votre activité avec un site performant et rentable</span>
    </h1>

    <section
      id="accueil"
      aria-labelledby="titre-accueil"
    >
      <div class="container">
        <div class="svg-container">
          <AccueilImg id="accueil_img"/>
        </div>
        <div class="text">
          <h2>
            <img
              :src="require(`@/assets/img/me.png`, )"
              alt="Photo de Cyril Heckmann"
              class="hello-icon"
            />
            <span>
              Vous avez un projet de site web ?
              <div>Vous cherchez un développeur ?</div>
            </span>
          </h2>
          <div class="description-me">
            <p class="description-value">
            <strong>Développeur web à Strasbourg depuis {{exp}} ans</strong>, je transforme vos idées
            en sites performants qui <strong>génèrent des résultats</strong>.
          </p>
          <ul class="benefits-list">
            <li>✓ Sites sur mesure (pas de Wordpress générique)</li>
            <li>✓ Technologies modernes et rapides (Vue.js, Python)</li>
            <li>✓ Référencement naturel optimisé dès le départ</li>
            <li>✓ Support technique inclus</li>
          </ul>
          <details class="about-details">
            <summary>En savoir plus sur mon parcours</summary>
            <p>
              Diplômé de l'IUT Robert Schuman, je suis actuellement développeur fullstack en CDI,
              ce qui garantit ma disponibilité et ma pérennité pour vos projets...
            </p>
          </details>
          </div>
        </div>
      </div>
    </section>
    <section
      id="services"
      aria-labelledby="titre-services"
    >
      <div class="container">
        <div class="text">
          <h2>
            Mes services
          </h2>
          <ul>
            <li :class="{'is-animated fade': !isMobile && notPreRendered, }">
              <div>
                <i class="fa fa-code" />
                <h3>Conception et développement</h3>
                <p>
                  Avec plus de {{exp}} ans d'expérience en développement de solutions web en tout genre,
                  je saurais répondre à tous vos besoins.
                </p>
              </div>
            </li>
            <li :class="{'is-animated fade': !isMobile && notPreRendered, }">
              <div>
                <i class="fa fa-server" />
                <h3>Gestion de l'hébergement</h3>
                <p>
                  Dormez tranquille : hébergement sécurisé en France + sauvegardes automatiques incluses.
                </p>
              </div>
            </li>
            <li :class="{'is-animated fade': !isMobile && notPreRendered, }">
              <div>
                <i class="fab fa-google" />
                <h3>Optimisation du référencement naturel</h3>
                <p>
                  Un code propre, une sémantique respectée ainsi que des mots-clés bien choisis
                  permettront à votre site d'apparaître dans les premiers résultats de recherche.
                </p>
              </div>
            </li>
            <li :class="{'is-animated fade': !isMobile && notPreRendered, }">
              <div>
                <i class="fa fa-lock" />
                <h3>Mise à jour de sécurité</h3>
                <p>
                  Un site internet doit être accessible 24h/24, les mises à jour de sécurité sont à installer
                  régulièrement afin d'éviter tout désagrément et pour garantir la sécurité de vos utilisateurs.
                </p>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </section>
    <section
      id="presentation"
      aria-labelledby="titre-presentation"
    >
      <div class="container">
        <div class="svg-container">
          <PresentationImg id="presentation_img"/>
        </div>
        <div class="text">
          <h2>
            Un site web est très important
            <div class="small">
              Quelques chiffres du
              <a
              href="https://www.francenum.gouv.fr/guides-et-conseils/strategie-numerique/
comprendre-le-numerique/barometre-france-num-2025-le"
              target="_blank"
              rel="noopener noreferrer"
              >
                "Baromètre France Num 2025"
              </a>
              par la DGE</div>
          </h2>
          <ul>
            <li>
              <h3>Se démarquer de la concurrence</h3>
              <p><strong>65 %</strong> des dirigeants affirment que leur entreprise
                <strong>possède un site internet de présentation</strong>.</p>
            </li>
            <li>
              <h3>
                Augmenter votre chiffre d'affaires
              </h3>
              <p>
                <strong>79 %</strong> des dirigeants déclarent que le numérique représente un bénéfice réel pour
                leur entreprise. Ce chiffre monte même à <strong>85 %</strong> pour les dirigeants de PME.
              </p>
            </li>
            <li>
              <h3>
                Investissement dans les projets numériques
              </h3>
              <p>
                En 2024, <strong>46 % des entreprises ont dépensé plus de 1000 €</strong>
                dans le numérique. Au total, <strong>79 %</strong> des structures disposent d'un budget dédié,
                dont <strong>29 %</strong> ont investi <strong>plus de 2000 €</strong> sur l'année écoulée.
              </p>
            </li>
          </ul>
        </div>
      </div>
    </section>
    <section
      id="portfolio"
      aria-labelledby="titre-portfolio"
    >
      <div class="container">
        <div class="text">
          <h2>
            Mes travaux
          </h2>
          <ul class="projects">
            <li
              v-for="projet in projets"
              :key="projet.nom"
              class="project"
            >
              <div class="carousel-img">
                <a
                  v-if="projet.url"
                  :href="projet.url"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <img
                    :src="require(`@/assets/img/${projet.img}.png`, )"
                    :alt="`Capture d'écran du site ${projet.nom}`"
                  />
                </a>
                <img
                  v-else
                  :src="require(`@/assets/img/${projet.img}.png`, )"
                  :alt="`Capture d'écran du site ${projet.nom}`"
                />
              </div>
              <h3 v-html="projet.nom" />
              <p class="sous-titre">{{projet.sousTitre}}</p>
              <p class="description">
                <span v-html="projet.description" />
                <a
                  v-if="projet.url"
                  :href="projet.url"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {{projet.url}}
                </a>
              </p>
            </li>
          </ul>
        </div>
      </div>
    </section>
    <section
      id="contact"
      aria-labelledby="titre-contact"
    >
      <div class="container">
        <div class="svg-container">
          <ContactImg id="contact_img"/>
        </div>
        <div class="text">
          <h2>Contactez-moi</h2>
          <ul>
            <li :class="{'is-animated fade': !isMobile && notPreRendered, }">
              <a
              href="mailto:cyrilheckmann@gmail.com"
              target="_blank"
              >
                <i class="fa fa-at" />
                cyrilheckmann@gmail.com
              </a>
            </li>
            <li :class="{'is-animated fade': !isMobile && notPreRendered, }">
              <a
              href="https://www.linkedin.com/in/cyril-heckmann/"
              rel="noopener noreferrer"
              target="_blank"
              >
                <i class="fab fa-linkedin" />
                Cyril Heckmann
              </a>
            </li>
            <li :class="{'is-animated fade': !isMobile && notPreRendered, }">
              <a
              href="https://discordapp.com/users/259381662897537024"
              rel="noopener noreferrer"
              target="_blank"
              >
                <i class="fab fa-discord" />
                @hectofr
              </a>
            </li>
          </ul>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
// import { Carousel, Slide } from "vue-carousel";
import AccueilImg from "@/components/icons/AccueilImg.vue";
import PresentationImg from "@/components/icons/PresentationImg.vue";
import ContactImg from "@/components/icons/ContactImg.vue";

export default {
  name: "Home",
  components: {
    AccueilImg,
    PresentationImg,
    ContactImg,
    // Carousel,
    // Slide,
  },
  data() {
    return {
      projets: [
        {
          img: "placeholder",
          nom: "Votre projet ?",
          sousTitre: "Conception, design, référencement, front, back...",
          description: `
          Un site web est un moyen de communication essentiel pour une entreprise, peu importe sa taille.
          `,
        },
        {
          img: "hawk",
          date: "2026",
          nom: "Hawk",
          sousTitre: "Conception complète du produit.",
          url: "",
          description: `
            Application web permettant le suivi de rapport d'erreur des applications d'une société à partir de
            Elastic APM.
          `,
        },
        {
          img: "shopping",
          date: "2025",
          nom: "Shopping List",
          sousTitre: "Application personnelle.",
          url: "",
          description: `
            Application web permettant la constitution de listes de courses avec plan en 3D du magasin et pathfinding.
          `,
        },
        {
          img: "upsf",
          date: "2024",
          nom: "Université Populaire sans frontières",
          sousTitre: "Corrections et nouvelles fonctionnalités.",
          url: "https://upsf.fr/",
          description: `
            Site web pour une association proposant divers cours.<br/>
            Paiement en ligne, gestion des cours, des inscriptions, des professeurs, des élèves, des salles...
          `,
        },
        {
          img: "cyclotruck",
          nom: "Cyclo&nbsp;Truck",
          date: "2022 - Aujourd'hui",
          sousTitre: "Design, front et back.",
          url: "https://cyclo-truck.fr",
          description: `
          Site web pour une TPE itinérante de réparation de vélos. Tarifs, services et catalogue de vélos.<br/>
          Optimisation du référencement.
          `,
        },
        {
          img: "lestentactrices",
          nom: "Les&nbsp;Tent'Actrices",
          date: "2022",
          sousTitre: "Back et aide front.",
          url: "https://lestentactrices.fr",
          description: "Site web pour une troupe de théatre proposant une connexion, un livre d'or...",
        },
        {
          img: "labsolu",
          nom: "L'&nbsp;Absolu",
          date: "2021",
          sousTitre: "Conception, design, front et back.",
          description: `
          Application PWA pour une salle de sport: suivi de ses performances en musculation, création de séance,
          visualisation sous forme de graphiques, contrôle de la musique de la salle...
          `,
        },
      ],
      handleResize: null,
      handleHeaderScroll: null,
      handleAnimationScroll: null,
    };
  },
  computed: {
    exp() {
      const start = new Date("01/07/2018");
      const now = new Date();
      const exp = new Date(now - start).getFullYear() - 1970;

      return exp - 1; // pour le "plus de x années..."
    },
    isMobile() {
      return this.windowWidth <= 720;
    },
    isTablet() {
      return this.windowWidth <= 1100;
    },
  },
  mounted() {
    this.handleResize = () => {
      this.windowWidth = window.innerWidth;
    };

    this.handleHeaderScroll = () => {
      const headerContainer = document.querySelector("#header_container");
      const header = document.querySelector("header");
      if (!header) return;
      const sticky = header.offsetTop + 10;

      if (window.pageYOffset > sticky) {
        if (headerContainer) {
          headerContainer.classList.add("sticky");
        }
        header.classList.add("sticky");
      } else {
        if (headerContainer) {
          headerContainer.classList.remove("sticky");
        }
        header.classList.remove("sticky");
      }
    };

    this.handleAnimationScroll = () => {
      const elements = document.querySelectorAll(".is-animated");
      elements.forEach((el) => {
        if (this.isInViewport(el)) {
          el.classList.add("active");
        }
      });
    };

    window.addEventListener("resize", this.handleResize);
    window.addEventListener("scroll", this.handleHeaderScroll);
    document.addEventListener("scroll", this.handleAnimationScroll);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("scroll", this.handleHeaderScroll);
    document.removeEventListener("scroll", this.handleAnimationScroll);
  },
  methods: {
    isInViewport(el) {
      const rect = el.getBoundingClientRect();
      return rect.top <= (window.innerHeight || document.documentElement.clientHeight);
    },
  },
};
</script>
